import { Alert, Box, IconButton, Typography } from "@mui/material"
import { EditOutlined } from "@mui-symbols-material/w300"
import { type FC, useState } from "react"

import { LegalInformationDialog } from "../../dialogs/CompanyLegalInfoDialog"
import useCompanyLegalInfo from "../../hooks/useCompanyLegalInfo"

import { useGetLocations } from "@/hooks/useGetLocations"
import { AddressFormat } from "@/types/common"
import { formatLocationAddress } from "@/utils/index"

const LegalInfoArea: FC<{ companyId: string }> = ({ companyId }) => {
  const { legalInfo, legalInfoFetching, refetchLegalInfo } = useCompanyLegalInfo({
    companyId,
  })

  const { locations, isLocationsFetching } = useGetLocations(companyId)

  const [isModalOpened, setIsModalOpened] = useState(false)

  const handleSubmit = () => {
    refetchLegalInfo()
    setIsModalOpened(false)
  }

  return (
    <>
      {locations.length === 0 && !isLocationsFetching && (
        <Alert
          severity='warning'
          className='px-6 py-4'
          classes={{
            message: "flex flex-col w-full",
          }}
          color='warning'
        >
          <Box className='flex w-full flex-col gap-2'>
            <p className='text-left text-sm font-normal leading-[17.5px] tracking-[0.17px] text-gray-700'>
              No locations are listed. Please add a location to update legal information.
            </p>
          </Box>
        </Alert>
      )}

      {!legalInfoFetching && (
        <Box className='flex flex-col justify-between rounded-md border border-solid border-gray-300 p-6'>
          <Box className='flex justify-between'>
            <Typography variant='subtitle1' className='mb-4'>
              Legal Information
            </Typography>
            {locations.length > 0 && (
              <IconButton
                color='primary'
                aria-label='edit product packaging information'
                onClick={() => setIsModalOpened(true)}
              >
                <EditOutlined />
              </IconButton>
            )}
          </Box>
          <Box className='mb-4 flex'>
            <Box className='w-1/2'>
              <Typography variant='subtitle2' className='mb-2'>
                Company Legal Name
              </Typography>
              <Typography variant='body2'>{legalInfo.legalName}</Typography>
            </Box>

            <Box className='w-1/2'>
              <Typography variant='subtitle2' className='mb-2'>
                Legal Address Location
              </Typography>
              <Typography variant='body2'>
                {legalInfo.legalLocation
                  ? formatLocationAddress(legalInfo.legalLocation, AddressFormat.LocationAndAddress)
                  : "-"}
              </Typography>
            </Box>
          </Box>
        </Box>
      )}

      {isModalOpened && (
        <LegalInformationDialog
          companyId={companyId}
          legalInfo={legalInfo}
          onClose={() => setIsModalOpened(false)}
          onSubmit={handleSubmit}
        />
      )}
    </>
  )
}

export default LegalInfoArea
